import { FieldArray, type FormikErrors, type FormikTouched, type FormikValues } from 'formik';
import React from 'react';
import Button from '../../../../components/button';
import FormSelect from '../../../../components/dropdown/formSelect';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';
import { type OptionType, type UserType } from '../../../../types/common';
import CustomDropdown from '../../../../components/custom-dropdown';

interface GroupProps {
  errors: FormikErrors<any> | any;
  touched: FormikTouched<any> | any;
  values: FormikValues;
  modalStatus: string;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  departmentOptions?: OptionType[];
  employeeTypeOptions?: OptionType[];
  jobTitleOptions?: UserType[];
  setFieldValue: any;
  setFieldTouched: any;
  handleChange: any;
}

const GroupOptions = ({
  errors,
  touched,
  values,
  modalStatus,
  departmentOptions,
  employeeTypeOptions,
  jobTitleOptions,
  handleRequest,
  handleChange,
  setFieldValue,
  setFieldTouched,
}: GroupProps): JSX.Element => {
  const handleUser = (userData: UserType, index: number): void => {
    const userInfo = values?.blackoutGroups[index]?.blackOutJobTitles;
    if (!(userInfo.includes(userData.value) === true)) {
      userInfo.push(userData.value);
    } else {
      userInfo.splice(userInfo.indexOf(userData.value), 1);
    }
    const group = values.blackoutGroups[index];
    const hasData = group?.customerDepartmentId != null || group?.employeeTypeId != null || userInfo.length > 0;
    setFieldValue(`blackoutGroups.${index}.groupData`, hasData);
    setFieldValue(`blackoutGroups.${index}.blackOutJobTitles`, [...userInfo]);
    setTimeout(() => setFieldTouched(`blackoutGroups.${index}.blackOutJobTitles`, true));
  };
  return (
    <div className='col-span-12' id="tradingBlackoutGroupOptionsContainer">
      <Heading variant='h5' title='Add Participant Groups' className='group-title' />
      <Heading
        variant='body'
        title='Select one or more below'
        className='group-sub-title'
        color={COLORS.Gray}
      />
      <div className='grid grid-cols-10 gap-x-8' id="tradingBlackoutGroupOptionsFormContainer">
        <FieldArray
          name='blackoutGroups'
          render={(arrayHelpers) => {
            return (
              <>
                {Boolean(values?.blackoutGroups) &&
                  values.blackoutGroups.length > 0 &&
                  values.blackoutGroups.map((friend: any, index: number) => (
                    <>
                      <FormSelect
                        containerClassName='col-span-3'
                        label='Department'
                        placeholder='Select'
                        values={values}
                        name={`blackoutGroups.${index}.customerDepartmentId`}
                        id={`blackoutGroups.${index}.customerDepartmentId`}
                        errorValue={errors?.blackoutGroups?.[index]?.customerDepartmentId}
                        touchedValue={touched?.blackoutGroups?.[index]?.customerDepartmentId}
                        arrValue={values?.blackoutGroups[index]?.customerDepartmentId}
                        setFieldValue={setFieldValue}
                        onChange={(e: any) => {
                          handleChange(e);
                          setFieldValue(`blackoutGroups.${index}.blackOutJobTitles`, []);
                          setFieldValue(`blackoutGroups.${index}.groupData`, true);
                          setTimeout(() =>
                            setFieldTouched(`blackoutGroups.${index}.customerDepartmentId`, true)
                          );
                        }}
                        errors={errors}
                        touched={touched}
                        allOptions={departmentOptions}
                        options={departmentOptions?.filter((item) => {
                          const currentValue = values.blackoutGroups[index]?.customerDepartmentId;
                          if (item.value === currentValue) return true;
                          const selectedByOthers = values.blackoutGroups
                            .filter((_: any, i: number) => i !== index)
                            .map((g: Record<string, string | string[]>) => g.customerDepartmentId);
                          return !selectedByOthers.includes(item.value);
                        })}
                        disabled={modalStatus === 'View' && true}
                        required
                      />
                      <FormSelect
                        containerClassName='col-span-3'
                        label='Employee Type '
                        placeholder='Select'
                        name={`blackoutGroups.${index}.employeeTypeId`}
                        id={`blackoutGroups.${index}.employeeTypeId`}
                        arrValue={values?.blackoutGroups[index]?.employeeTypeId}
                        errorValue={errors?.blackoutGroups?.[index]?.employeeTypeId}
                        touchedValue={touched?.blackoutGroups?.[index]?.employeeTypeId}
                        onChange={(e: any) => {
                          handleChange(e);
                          setFieldValue(`blackoutGroups.${index}.blackOutJobTitles`, []);
                          setFieldValue(`blackoutGroups.${index}.groupData`, true);
                          setTimeout(() =>
                            setFieldTouched(`blackoutGroups.${index}.employeeTypeId`, true)
                          );
                        }}
                        errors={errors}
                        touched={touched}
                        allOptions={employeeTypeOptions}
                        options={employeeTypeOptions?.filter((item) => {
                          const currentValue = values.blackoutGroups[index]?.employeeTypeId;
                          if (item.value === currentValue) return true;
                          const selectedByOthers = values.blackoutGroups
                            .filter((_: any, i: number) => i !== index)
                            .map((g: Record<string, string | string[]>) => g.employeeTypeId);
                          return !selectedByOthers.includes(item.value);
                        })}
                        values={values}
                        setFieldValue={setFieldValue}
                        disabled={modalStatus === 'View' && true}
                        required
                      />
                      <div className='col-span-3' id={`tradingBlackoutJobTitleContainer${index}`}>
                        <CustomDropdown
                          title={'Job Title'}
                          data={jobTitleOptions}
                          errorMessage={errors?.blackoutGroups?.[index]?.blackOutJobTitles}
                          touchedValue={touched?.blackoutGroups?.[index]?.blackOutJobTitles}
                          handleUser={(e) => {
                            handleUser(e, index);
                          }}
                          onClear={() => {
                            setFieldValue(`blackoutGroups.${index}.blackOutJobTitles`, []);
                            setTimeout(() => setFieldTouched(`blackoutGroups.${index}.blackOutJobTitles`, true));
                          }}
                          checkedList={values?.blackoutGroups[index]?.blackOutJobTitles ?? []}
                          cssStyles={{
                            width: '225px',
                            height:
                              jobTitleOptions != null && jobTitleOptions?.length > 5
                                ? '230px'
                                : 'auto',
                            contentHeight:
                              jobTitleOptions != null && jobTitleOptions?.length > 5
                                ? '200px'
                                : 'auto',
                            padding: '0',
                            verticalGap: '8',
                            column: 1,
                          }}
                          toggleItems={{
                            showSearch: false,
                            showTotal: false,
                            displaySelected: true,
                          }}
                          disabled={modalStatus === 'View' && true}
                          virtualize
                        />
                      </div>
                      {modalStatus === 'View'
                        ? null
                        : values.blackoutGroups.length > 1 && (
                          <div
                            className='flex items-center ap-2'
                            role='button'
                            id={`deleteTradingBlackoutGroupOption${index}`}
                            onClick={() => {
                              arrayHelpers.remove(index);
                            }}>
                            <span className='material-symbols-outlined text-red-600'>delete</span>
                            <Heading variant='body-s' title='Delete' color={`${COLORS.Gray}`} />
                          </div>
                        )}
                    </>
                  ))}
                {modalStatus !== 'View' && (
                  <div className='col-span-12' id="addAnotherTradingBlackoutCriteriaContainer">
                    <Button
                      title='Add another criteria'
                      type='button'
                      variant='primary-dark'
                      icon='group_add'
                      btnClassName='w-full'
                      id="addAnotherTradingBlackoutCriteriaButton"
                      onClick={() => {
                        arrayHelpers.push({
                          id: null,
                          customerDepartmentId: null,
                          employeeTypeId: null,
                          blackOutJobTitles: [],
                          groupData: false,
                        });
                      }}
                    />
                  </div>
                )}
              </>
            );
          }}
        />
      </div>
    </div>
  );
};

export default GroupOptions;
